/**
 * navigation/platformAuthRedirect.ts
 * ──────────────────────────────────────────────────────────────────────────
 * SK Queue does NOT own authentication. The SK Platform owns the only login /
 * signup / forgot-password screens for every product. When this app has no valid
 * session we bounce the user to the centralized platform auth, carrying the
 * product handoff:
 *     ?product=queue&return_to=<where they were>&state=<nonce>
 * plus a short-lived `sk_auth_state` cookie (parent-domain scoped) the platform
 * verifies (CSRF / first-party initiation). After auth, the platform redirects
 * back here, logged in via the shared session cookie.
 *
 * Web-only; no-op on native. Ported from sk-catalog-frontend (product=catalog).
 */
import { Platform } from 'react-native';

const IS_LOCAL =
  typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
const PLATFORM_AUTH_URL = IS_LOCAL
  ? 'http://localhost:3009/'
  : 'https://platform.staging.softknack.com/';
const PRODUCT = 'queue';
const STATE_COOKIE = 'sk_auth_state';

const isWeb = (): boolean => Platform.OS === 'web' && typeof window !== 'undefined';

function randomState(): string {
  const bytes = new Uint8Array(16);
  (window.crypto as Crypto).getRandomValues(bytes);
  let hex = '';
  for (let i = 0; i < bytes.length; i += 1) hex += bytes[i].toString(16).padStart(2, '0');
  return hex;
}

function setStateCookie(state: string): void {
  const parts = window.location.hostname.split('.');
  const domain = parts.length >= 2 && parts[parts.length - 1] !== 'localhost'
    ? `; domain=.${parts.slice(-2).join('.')}`
    : '';
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${STATE_COOKIE}=${state}; Max-Age=600; path=/; SameSite=Lax${domain}${secure}`;
}

// Guard against firing twice within one page load (boot probe + a parallel 401).
let _redirecting = false;

/** Bounce to the centralized platform auth, returning here after sign-in. */
export function redirectToPlatformAuth(): void {
  if (!isWeb() || _redirecting) return;
  _redirecting = true;

  const state = randomState();
  setStateCookie(state);

  const url = new URL(PLATFORM_AUTH_URL);
  url.searchParams.set('product', PRODUCT);
  url.searchParams.set('return_to', window.location.href);
  url.searchParams.set('state', state);
  window.location.assign(url.toString());
}

/**
 * Send a signed-in user whose org did not buy Queue to the platform's
 * "not in your plan" page (see lib/planGate). The session stays: the org's own
 * console still calls Queue. `replace`, so Back does not land on a Queue admin
 * that would only send them here again.
 */
export function redirectToNotInPlan(): void {
  if (!isWeb() || _redirecting) return;
  _redirecting = true;
  const url = new URL('not-in-plan', PLATFORM_AUTH_URL);
  url.searchParams.set('product', PRODUCT);
  window.location.replace(url.toString());
}
